import { useNavigate } from 'react-router-dom'
import { TriangleAlert, Zap, Wind } from 'lucide-react'
import { DotGlobe } from './DotGlobe'
import { useGlobeHero, ClockToggle, SunArc, PointSheet } from './globe-chrome'
import { spcLevel, type SpcCategory } from '@/features/pulse/pulse-data'
import { threatColor } from '@/lib/criteria/levels'
import { fmtTimeClock } from '@/lib/time'
import { hasR2 } from '@/config'
import './globe.css'

/**
 * The Nationwide signature: the live DOT GLOBE in a contained stage, with the day/date
 * block, 12h/24h toggle, sunrise/sunset sun-arc, and a live legend. (The Home view uses
 * the full-bleed integrated `HomeGlobe` instead.)
 */
export function GlobeHero({ location }: { location?: { lat: number; lon: number; label?: string } }) {
  const nav = useNavigate()
  const { points, alertTotal, tropicalCount, spcCategory, clock, toggleClock, now, selected, setSelected, sun, prog } =
    useGlobeHero(location)

  const weekday = now.toLocaleDateString('en-US', { weekday: 'long' })
  const datestr = now.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })
  const time = fmtTimeClock(now, clock)
  const activeCount = points.length

  return (
    <section className="globe-hero globe-hero--nationwide" aria-label="Live globe">
      <div className="globe-hero__top">
        <div className="globe-hero__date">
          <span className="globe-hero__weekday">{weekday}</span>
          <span className="globe-hero__clock num">{time}</span>
          <span className="globe-hero__day">{datestr}</span>
        </div>
        <ClockToggle clock={clock} onToggle={toggleClock} />
      </div>

      <div className="globe-hero__stage">
        <DotGlobe
          points={points}
          userLat={location?.lat}
          userLon={location?.lon}
          onSelect={setSelected}
          ariaLabel={`Rotating globe showing ${activeCount} active weather point${
            activeCount === 1 ? '' : 's'
          } worldwide`}
        />
        {selected && (
          <PointSheet
            point={selected}
            onClose={() => setSelected(null)}
            onOpen={() => {
              const href = selected.href
              setSelected(null)
              nav(href)
            }}
          />
        )}
      </div>

      {sun && prog && location ? <SunArc sun={sun} prog={prog} clock={clock} /> : null}

      <div className="globe-hero__legend">
        <span className="globe-hero__live">
          <span className="live-dot" /> Live
        </span>
        {hasR2 && (
          <>
            <LegendStat icon={<Wind size={14} />} color="var(--status-tropics)" value={tropicalCount} label="tropical" />
            <LegendStat
              icon={<Zap size={14} />}
              color={
                spcCategory && spcCategory !== 'NONE'
                  ? threatColor(spcLevel(spcCategory as SpcCategory))
                  : 'var(--text-3)'
              }
              value={spcCategory && spcCategory !== 'NONE' ? spcCategory : null}
              label="severe"
            />
          </>
        )}
        <LegendStat icon={<TriangleAlert size={14} />} color="var(--status-warn)" value={alertTotal} label="US alerts" />
      </div>
      <p className="globe-hero__hint">
        {activeCount > 0 ? 'Tap a glowing point to open it · drag to spin' : 'Tracking active weather worldwide'}
      </p>
    </section>
  )
}

function LegendStat({
  icon,
  color,
  value,
  label,
}: {
  icon: React.ReactNode
  color: string
  value: number | string | null
  label: string
}) {
  return (
    <span className="legend-stat" style={{ '--c': color } as React.CSSProperties}>
      <span className="legend-stat__icon">{icon}</span>
      <span className="legend-stat__val num">{value ?? '—'}</span>
      <span className="legend-stat__label">{label}</span>
    </span>
  )
}
